<script lang="ts">
    import type {DataFile} from "../models/data.interface";
    import moment from "moment";

    export let datasetMetadata;
    export let file;

    let dataPath = new URL(process.env.PREVIEW_DATA_URL);
    // let dataPath = "https://www.ncei.noaa.gov/access/services/data/v1";

    let minMaxMap: Map<string, number>;

    let availableDays: string[];
    let availableMonths: string[];
    let availableYears: number[];
    let isYearless = false;
    let selectedYear: string;
    let selectedFile: DataFile;
    let selectedMonth: string;
    let selectedDay: string;
    let selectedStationId: string;
    let selectedBoundingBox: string[];
    let previewDataServiceUrl: string;

    // TODO: trying to replace the angular @Input(), there might be a better way to do this
    //
    // @Input()
    // set selectedPreviewFile(file: DataFile) {
    //    if (file != null) {
    //        this.setSelectedPreviewFile(file);
    //    }
    // }
    $: if (file != null) {
        setSelectedPreviewFile(file);
        // console.log("FILE", file);
        // console.count("counter: ")
    }

    function onYearChange(event: any): void {
        const incomingYear = event.target.value;
        selectedYear = incomingYear;


        // Regenerate available months
        availableMonths = generateAvailableMonths(incomingYear);

        // If selected month is no longer valid, set to first available month
        if (datasetMetadata?.previewoptions.dateselection.includes("month") && datasetMetadata?.previewoptions.dateselection.includes("day")) {
            selectedMonth = availableMonths[0];
        }

        // Same thing with the days
        availableDays = generateAvailableDays(
            selectedFile.startDate,
            selectedFile.endDate,
            incomingYear,
            selectedMonth
        );

        if (datasetMetadata?.previewoptions.dateselection.includes("day")) {
            selectedDay = availableDays[0];
        }
    }

    function onMonthChange(event: any): void {
        const incomingMonth = event.target.value;
        selectedMonth = incomingMonth;

        // Regenerate available days
        availableDays = generateAvailableDays(
            selectedFile.startDate,
            selectedFile.endDate,
            selectedYear,
            incomingMonth
        );

        // If selected day is no longer valid, set to first available day
        if (datasetMetadata?.previewoptions.dateselection.includes("day") &&
            availableDays.includes(selectedDay)
        ) {
            selectedDay = availableDays[0];
        }
    }

    function onDayChange(event: any): void {
        selectedDay = event.target.value;
    }

    function getPreviewDataServiceUrl(): string {
        // let previewDataServiceUrl: string;
        let startDate: string,
            startYear: string,
            startMonth: string,
            startDay: string,
            endDate: string,
            endYear: string,
            endMonth: string,
            endDay: string,
            stationId: string,
            boundingBox: string;

        if (datasetMetadata != null && selectedFile != null) {
            // Build the date based off what the user has selected
            if (datasetMetadata?.previewoptions.dateselection || isYearless) {
                startYear = selectedYear;
                endYear = selectedYear;
            } else {
                startYear = minMaxMap.get('minYear').toString();
                endYear = minMaxMap.get('maxYear').toString();
            }

            if (selectedMonth == null) {
                startMonth = '01';
                endMonth = '12';
            } else {
                startMonth = selectedMonth;
                endMonth = selectedMonth;
            }

            if (selectedDay == null) {
                startDay = '01';
                endDay = getLastDayOfMonth(selectedYear, endMonth);
            } else {
                startDay = selectedDay;
                endDay = selectedDay;
            }

            startDate = `${startYear}-${startMonth}-${startDay}`;
            endDate = `${endYear}-${endMonth}-${endDay}`;

            // Set the station
            stationId = selectedStationId;
            if (stationId == null) {
                stationId = selectedFile?.stations[0].id;
            }

            // default boundingBox to global extent
            boundingBox = '90,-180,-90,180';
            if (selectedBoundingBox) {
                boundingBox = selectedBoundingBox.join(',');
            }

            // Build data service URL
            previewDataServiceUrl =
                dataPath +
                `?dataset=${datasetMetadata.id}` +
                `&startDate=${startDate}` +
                `&endDate=${endDate}` +
                `&stations=${stationId}` +
                `&boundingBox=${boundingBox}` +
                `&format=pdf`;
        }
        return previewDataServiceUrl;
    }

    export function setSelectedPreviewFile(file: DataFile): void {
      // console.log(file);

        // Set file and clear selected values
        isYearless = false;
        minMaxMap = getMinMaxDateRange(file.startDate, file.endDate);
        selectedBoundingBox = null;
        selectedDay = null;
        selectedFile = file;
        selectedMonth = null;
        selectedStationId = null;
        selectedYear = null;

        // Detect if the dataset is yearless (like the normals)
        isYearless = isFileYearless(file);

        // Populate available years
        availableYears = [];
        if (!isYearless) {
            for (let year = minMaxMap.get('minYear'); year <= minMaxMap.get('maxYear'); year++) {
                availableYears.push(year);
            }
        }

        // Populate selected variables with intelligent guess
        selectedYear = minMaxMap.get('minYear').toString();
        selectedMonth = `0${minMaxMap.get('minMonth').toString()}`.slice(-2); // Pad with leading zero
        selectedDay = `0${minMaxMap.get('minDay').toString()}`.slice(-2); // Pad with leading zero

        // Generate available months/days
        availableMonths = generateAvailableMonths(selectedYear);
        availableDays = generateAvailableDays(
            file.startDate,
            file.endDate,
            selectedYear,
            selectedMonth
        );

        // Special case for yearless datasets.
        if (isYearless) {
            selectedYear = '0001';
        }

        // Set unused values to null
        if (datasetMetadata?.previewoptions.dateselection.includes("year") &&
            datasetMetadata?.previewoptions.dateselection.length === 1
        ) {
            selectedMonth = null;
            selectedDay = null;
        } else if (datasetMetadata?.previewoptions.dateselection.includes("month")
            && (datasetMetadata?.previewoptions.dateselection.length === 1 ||
                datasetMetadata?.previewoptions.dateselection.length === 2)
        ) {
            selectedDay = null;
        }

        // TODO: the station selection is only for global-marine and that's going to require a special page so I'm going to include that in this
        // Set selected station
        // if (datasetMetadata.interfaceoptions.previewoptions.stationselection) {
        //     const fileStationIds = file.fields?.stations.map(s => s.id);
        //     let searchStationIds = searchState.params['stations'];
        //     if (searchStationIds) {
        //         searchStationIds = searchStationIds.filter(id => fileStationIds.indexOf(id) > -1).sort();
        //     }
        //     selectedStationId = searchStationIds ? searchStationIds[0] : file.fields?.stations[0].id;
        // }

        // Set selected bounding box
        if (selectedFile.boundingPoints) {
            if (selectedFile.boundingPoints.length === 1) {
                selectedBoundingBox = [
                    String(selectedFile.boundingPoints[0].coordinates[1]),
                    String(selectedFile.boundingPoints[0].coordinates[0]),
                    String(selectedFile.boundingPoints[0].coordinates[1]),
                    String(selectedFile.boundingPoints[0].coordinates[0])
                ];
            } else {
                // TODO: this is only used for global-marine and needs to be changed once that gets it's special page
                selectedBoundingBox = [
                    String(selectedFile.boundingPoints[0][0]),
                    String(selectedFile.boundingPoints[0][1]),
                    String(selectedFile.boundingPoints[1][0]),
                    String(selectedFile.boundingPoints[1][1])
                ];
            }
        }
    }

    function isFileYearless(file: DataFile): boolean {
        return file.startDate.startsWith('0001');
    }

    function range(start: number, end: number): string[] {
        const range = [];
        for (let i = start; i <= end; i++) {
            const iString = `0${i}`.slice(-2); // Pad with leading zero
            range.push(iString);
        }
        return range;
    }

    function getMinMaxDateRange(start: string, end: string): Map<string, number> {
        const minMaxMap = new Map<string, number>();

        minMaxMap.set('minYear', +start.substring(0, 4));
        minMaxMap.set('maxYear', +end.substring(0, 4));
        minMaxMap.set('minMonth', +start.substring(5, 7));
        minMaxMap.set('maxMonth', +end.substring(5, 7));
        minMaxMap.set('minDay', +start.substring(8, 10));
        minMaxMap.set('maxDay', +end.substring(8, 10));

        return minMaxMap;
    }

    function getLastDayOfMonth(year: string, month: string): string {
        return moment(`${year}-${month}-01`, 'YYYY-MM-DD').endOf('month').format('DD');
    }

    function generateAvailableMonths(year: string): string[] {
        let months = range(1, 12);

        if (+year === minMaxMap.get('minYear')) {
            if (minMaxMap.get('minYear') === minMaxMap.get('maxYear')) {
                months = range(minMaxMap.get('minMonth'), minMaxMap.get('maxMonth'));
            } else {
                months = range(minMaxMap.get('minMonth'), 12);
            }
        } else if (+year === minMaxMap.get('maxYear')) {
            months = range(1, minMaxMap.get('maxMonth'));
        }

        return months;
    }

    function generateAvailableDays(start: string, end: string, year: string, month: string): string[] {
        const minYearMonth = start.substring(0, 7);
        const maxYearMonth = end.substring(0, 7);
        const curYearMonth = `${year}-${month}`;
        const selectedMonthMax = getLastDayOfMonth(year, month);
        let days = range(1, +selectedMonthMax);

        if (curYearMonth === minYearMonth && curYearMonth === maxYearMonth) {
            days = range(minMaxMap.get('minDay'), minMaxMap.get('maxDay'));
        } else if (curYearMonth === minYearMonth) {
            days = range(minMaxMap.get('minDay'), +selectedMonthMax);
        } else if (curYearMonth === maxYearMonth) {
            days = range(1, minMaxMap.get('maxDay'));
        }

        return days;
    }
</script>

<div class="d-flex align-items-end my-3">
    {#if datasetMetadata?.previewoptions.dateselection.includes("year")}
        <div class="mx-1">
            <label for="previewYearSelect" class="form-label">Year</label>
            <select
                    class="form-select form-select-sm"
                    id="previewYearSelect"
                    aria-label="Preview year select"
                    bind:value={selectedYear}
                    on:change={onYearChange}
            >
                {#each availableYears || [] as y}
                    <option value={String(y)}>{y}</option>
                {/each}
            </select>
        </div>
    {/if}
    {#if datasetMetadata?.previewoptions.dateselection.includes("month")}
        <div class="mx-1">
            <label for="previewMonthSelect" class="form-label">Month</label>
            <select
                    class="form-select form-select-sm"
                    id="previewMonthSelect"
                    aria-label="Preview month select"
                    bind:value={selectedMonth}
                    on:change={onMonthChange}
            >
                {#each availableMonths || [] as m}
                    <option value={m}>{m}</option>
                {/each}
            </select>
        </div>
    {/if}
    {#if datasetMetadata?.previewoptions.dateselection.includes("day")}
        <div class="mx-1">
            <label for="previewDaySelect" class="form-label">Day</label>
            <select
                    class="form-select form-select-sm"
                    id="previewDaySelect"
                    aria-label="Preview day select"
                    bind:value={selectedDay}
                    on:change={onDayChange}
            >
                {#each availableDays || [] as d}
                    <option value={d}>{d}</option>
                {/each}
            </select>
        </div>
    {/if}

    <!-- TODO: the station selection is only for global-marine and that's going to require a special page so I'm going to include that in this -->
    {#if datasetMetadata?.previewoptions.stationselection}
        <div class="mx-1">
            <label for="previewStationSelect" class="form-label">Station</label>
            <select class="form-select form-select-sm" id="previewStationSelect" aria-label="Preview station select">
                <option selected>Station</option>
                <!--{#each selectedFile?.stations || [] as s}-->
                <!--    <option value={s?.id}>{s?.name ? s.name : s.id}</option>-->
                <!--{/each}-->
            </select>
        </div>
    {/if}
    <div>
    <a class="btn ae-primary btn-sm"
       target="_blank"
       rel="noreferrer"
       on:click={() => getPreviewDataServiceUrl()}
       href={previewDataServiceUrl}
    >
        <i class="bi bi-file-earmark-text-fill"></i>
        Preview Data
    </a>
    </div>
</div>
